import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Navbar from '../../components/Navbar/Navbar';
import { supabase } from '../../lib/supabaseClient';
import RequirementForm from './RequirementForm';
import RequirementDuplicateForm from './RequirementDuplicateForm';
import { FilterPickerModal, SpecializationPickerModal, EstimatorPickerModal, StatusPickerModal, StatusFilterModal, BilledFilterModal, DatePickerModal, DevPickerModal, RootPickerModal, COMMENT_MAXLEN } from './RequirementPickers';
import './Requirements.css';

const PAGE_SIZE = 30;

/* Filter sentinel: requirements with no billing type assigned */
const BILLING_TYPE_NONE = '__none__';

/* ── Icons ────────────────────────────────────────── */
function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconChevronLeft() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconChevronRight() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function IconExtLink() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function IconClearFilter() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

/* ── Toast notification ──────────────────────────── */
function Toast({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  if (!toast) return null;

  return (
    <div className={`req-toast req-toast--${toast.type}`}>
      {toast.type === 'success' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
      )}
      <span>{toast.message}</span>
    </div>
  );
}

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ── Comment view modal ──────────────────────────────
   Read-only view of a requirement's full comment, opened by clicking a
   non-empty (clamped) Comentarios cell outside edit mode. Line breaks are
   preserved via white-space: pre-wrap. `row` is looked up live from `rows`
   by the caller, so if the row is deleted by realtime the modal vanishes. */
function CommentViewModal({ row, onClose }) {
  useEffect(() => {
    if (!row) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [row, onClose]);

  if (!row || !row.comment) return null;

  return (
    <div className="req-picker-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="req-picker-modal req-comment-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Comentarios — {row.reference_id}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>
        <div className="req-comment-modal-body">
          <p className="req-comment-modal-text">{row.comment}</p>
        </div>
      </div>
    </div>
  );
}

/* ── Comment edit modal ──────────────────────────────
   Textarea editor for the Comentarios field (table + detail edit modes).
   Enter inserts a line break; saving is explicit via the Guardar button (or
   Ctrl/Cmd+Enter). Escape cancels. A backdrop click only cancels while the
   text is unchanged — once dirty it is ignored so a stray click can't throw
   away a long comment. */
function CommentEditModal({ open, folio, value, original, onChange, onSave, onClose }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const dirty = value !== (original ?? '');

  return (
    <div className="req-picker-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !dirty) onClose(); }}>
      <div className="req-picker-modal req-comment-modal">
        <div className="req-picker-header">
          <h3 className="req-picker-title">Editar Comentarios{folio ? ` — ${folio}` : ''}</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar">
            <IconClose />
          </button>
        </div>
        <div className="req-comment-modal-body">
          <textarea
            ref={textareaRef}
            className="req-comment-textarea"
            rows={8}
            maxLength={COMMENT_MAXLEN}
            placeholder="Comentarios adicionales…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                onSave();
              }
            }}
          />
        </div>
        <div className="req-dev-picker-footer">
          <span className="req-dev-picker-count">{value.length}/{COMMENT_MAXLEN}</span>
          <div className="req-dev-picker-actions">
            <button className="req-dev-picker-cancel" onClick={onClose}>Cancelar</button>
            <button className="req-dev-picker-confirm" onClick={onSave}>Guardar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ── Helpers ──────────────────────────────────────── */
function formatDate(val) {
  if (!val) return '—';
  /* Noon, not midnight: parsed as local time, midnight lands on the previous
     day in MX for devices east of UTC-6 (same convention as DatePickerModal) */
  const d = new Date(val + (val.includes('T') ? '' : 'T12:00:00'));
  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Monterrey',
  });
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function getPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
}

/* ── Shared select string (used by fetchData AND refetchRow) ─────────── */
const REQUIREMENTS_SELECT = `
  *,
  project:projects!project_id(id, name, client_id, client:clients!client_id(id, name)),
  status_rel:status!status_id(id, name, color),
  status_client_rel:status!status_client_id(id, name, color),
  specialization:specializations!specialization_id(id, name),
  billing_type:billing_types!billing_type_id(id, name),
  estimator:users!estimator_user_id(id, full_name),
  leader:users!leader_user_id(id, full_name),
  requirement_devs(user_id, user:users!user_id(id, full_name, roles(name)))
`;

/* ── Icons for detail view ─────────────────────────── */
function IconArrowLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconClipboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}

function IconFileText() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconBarChart() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function IconComment() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconPencilSmall() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/* ── Requirement Detail View ─────────────────────── */
/* Exported for reuse in RequirementsDashboard (read-only full view).
   When `editControls` is supplied (from the Requirements table view) the
   visible, role-permitted fields become editable in place — reusing the exact
   same column permissions, save handlers, modals and PA notifications as the
   table's "Editar Campos" mode. Omitting `editControls` keeps it read-only. */
export function RequirementDetail({ row, rootRefMap, isGerenteOrLider, onBack, editControls }) {
  const r = row;
  const ec = editControls || null;
  const editing = !!ec?.editMode;

  function fmtDate(val) {
    if (!val) return '—';
    /* Noon, not midnight — see formatDate */
    const d = new Date(val + (val.includes('T') ? '' : 'T12:00:00'));
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'America/Monterrey' });
  }

  /* ── Editable wrapper ──
     Given a field name and its read-only display node, returns either the plain
     node (not editing, or the field isn't editable for this role) or a clickable
     affordance that drives the parent's edit handlers. Permission comes straight
     from `editableColsByField` (built from the table's `columns`), so a
     Desarrollador only ever gets progress/status/comment, etc. */
  function editable(fieldName, valueNode) {
    if (!ec || !editing) return valueNode;
    if (ec.canRowEdit === false) return valueNode;
    const col = ec.editableColsByField[fieldName];
    if (!col) return valueNode;

    const isEditingThis = ec.editingCell?.rowId === r.id && ec.editingCell?.field === col.field;
    const isInline = col.editType === 'text' || col.editType === 'number' || col.editType === 'progress';

    if (isInline && isEditingThis) {
      return (
        <input
          autoFocus
          className="req-detail-edit-input"
          type={col.editType === 'text' ? 'text' : 'number'}
          value={ec.editValue}
          maxLength={col.editConfig?.maxLength}
          min={col.editConfig?.min}
          max={col.editConfig?.max}
          step={col.editConfig?.step || (col.editType === 'number' ? '1' : undefined)}
          onChange={(e) => ec.setEditValue(e.target.value)}
          onBlur={() => ec.commitEdit(r, col)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') ec.cancelEditing();
          }}
        />
      );
    }

    const onActivate = col.editType === 'toggle'
      ? () => ec.saveCell(r.id, col.field, !r[col.field], 'toggle')
      : () => ec.startEditing(r, col);

    return (
      <div
        className="req-detail-editable"
        role="button"
        tabIndex={0}
        onClick={onActivate}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
      >
        {valueNode}
        <span className="req-detail-edit-cue" aria-hidden="true"><IconPencilSmall /></span>
      </div>
    );
  }

  /* True when a field is editable in the current edit session (used to force a
     clickable placeholder for fields that render nothing when empty). */
  const canEdit = (fieldName) => editing && ec?.canRowEdit !== false && !!ec?.editableColsByField[fieldName];

  const progress = r.progress_percentage != null ? r.progress_percentage : null;

  const statusBadge = (s) => {
    if (!s) return <span className="req-detail-empty">Sin estatus</span>;
    return (
      <span className="req-status-badge" style={{ backgroundColor: s.color, color: getContrastColor(s.color) }}>
        {s.name}
      </span>
    );
  };

  const devs = (r.requirement_devs || []).filter((d) => d.user?.full_name);

  const devsNode = devs.length > 0 ? (
    <div className="req-devs-pills">
      {devs.map((d, i) => {
        const roleName = d.user?.roles?.name;
        const isLider = roleName === 'Líder' || roleName === 'Gerente';
        return (
          <span key={d.user_id ?? i} className={`req-dev-pill${isLider ? ' req-dev-pill--lider' : ' req-dev-pill--dev'}`}>
            {d.user.full_name}
          </span>
        );
      })}
    </div>
  ) : (
    <span className="req-detail-empty">Sin desarrolladores asignados</span>
  );

  const isFetchingUrl = ec?.fetchingUrls?.has(r.id);

  return (
    <div className="req-detail">
      {/* ── Toolbar: back + edit toggle ── */}
      <div className="req-detail-toolbar">
        <button className="req-detail-back" onClick={onBack}>
          <IconArrowLeft />
          <span>Regresar</span>
        </button>
        <div className="req-detail-toolbar-actions">
          {ec?.canDuplicate && (
            <button
              type="button"
              className="req-detail-dup-btn"
              onClick={() => ec.onDuplicate(r)}
            >
              <IconCopy />
              <span>Duplicar Requerimiento</span>
            </button>
          )}
          {ec?.canEditFields && ec?.canRowEdit !== false && (
            <button
              className={`req-detail-edit-btn${editing ? ' req-detail-edit-btn--active' : ''}`}
              onClick={() => { ec.setEditMode(!editing); ec.cancelEditing(); }}
            >
              <IconEdit />
              <span>{editing ? 'Dejar de Editar' : 'Editar Requerimiento'}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Header card ── */}
      <div className="req-detail-header">
        <div className="req-detail-header-top">
          <div className="req-detail-folio-wrap">
            <h1 className="req-detail-folio">{r.reference_id}</h1>
            {(r.root_requirement_id || canEdit('root_requirement_id')) && (
              editable('root_requirement_id', (
                <span className="req-detail-root-badge">
                  Root: {r.root_requirement_id ? (rootRefMap[r.root_requirement_id] ?? '—') : '—'}
                </span>
              ))
            )}
          </div>
          <div className="req-detail-statuses">
            <div className="req-detail-status-group">
              <span className="req-detail-status-label">Estatus</span>
              {editable('status_id', statusBadge(r.status_rel))}
            </div>
            {isGerenteOrLider && (
              <div className="req-detail-status-group">
                <span className="req-detail-status-label">Estatus Cliente</span>
                {editable('status_client_id', statusBadge(r.status_client_rel))}
              </div>
            )}
          </div>
        </div>
        {canEdit('description') ? (
          <div className="req-detail-description-edit">
            {editable('description', (
              <p className="req-detail-description">
                {r.description || <span className="req-detail-placeholder">Agregar descripción…</span>}
              </p>
            ))}
          </div>
        ) : (
          r.description && <p className="req-detail-description">{r.description}</p>
        )}
      </div>

      {/* ── Info grid ── */}
      <div className="req-detail-grid">
        {/* ── General info card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconClipboard />
            <h2>Información General</h2>
          </div>
          <div className="req-detail-card-body">
            <div className="req-detail-field">
              <span className="req-detail-field-label">Cliente</span>
              <span className="req-detail-field-value">{r.project?.client?.name ?? '—'}</span>
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Proyecto</span>
              <span className="req-detail-field-value">{r.project?.name ?? '—'}</span>
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Especialidad</span>
              {editable('specialization_id', <span className="req-detail-field-value">{r.specialization?.name ?? '—'}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Tipo de Requerimiento</span>
              {editable('requirement_type', <span className="req-detail-field-value">{r.requirement_type ?? '—'}</span>)}
            </div>
            {isGerenteOrLider && (
              <div className="req-detail-field">
                <span className="req-detail-field-label">Tipo de Facturación</span>
                {editable('billing_type_id', <span className="req-detail-field-value">{r.billing_type?.name ?? '—'}</span>)}
              </div>
            )}
            <div className="req-detail-field">
              <span className="req-detail-field-label">Módulo</span>
              {editable('module', <span className="req-detail-field-value">{r.module ?? '—'}</span>)}
            </div>
          </div>
        </div>

        {/* ── People card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconUsers />
            <h2>Personas</h2>
          </div>
          <div className="req-detail-card-body">
            <div className="req-detail-field">
              <span className="req-detail-field-label"><IconUser /> Líder</span>
              {editable('leader_user_id', <span className="req-detail-field-value">{r.leader?.full_name ?? '—'}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label"><IconUser /> Estimador</span>
              {editable('estimator_user_id', <span className="req-detail-field-value">{r.estimator?.full_name ?? '—'}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label"><IconUser /> Funcional</span>
              {editable('functional_user_name', <span className="req-detail-field-value">{r.functional_user_name ?? '—'}</span>)}
            </div>
            <div className="req-detail-field req-detail-field--full">
              <span className="req-detail-field-label">Desarrolladores</span>
              {editable('requirement_devs', devsNode)}
            </div>
          </div>
        </div>

        {/* ── Dates card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconCalendar />
            <h2>Fechas</h2>
          </div>
          <div className="req-detail-card-body">
            <div className="req-detail-field">
              <span className="req-detail-field-label">Creación del Folio</span>
              <span className="req-detail-field-value">{fmtDate(r.created_at)}</span>
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Estimación VoBo</span>
              {editable('VoBo_estimated_date', <span className="req-detail-field-value">{fmtDate(r.VoBo_estimated_date)}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Inicio Construcción</span>
              {editable('construction_start_date', <span className="req-detail-field-value">{fmtDate(r.construction_start_date)}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Entrega Desarrollador</span>
              {editable('dev_delivery_date', <span className="req-detail-field-value">{fmtDate(r.dev_delivery_date)}</span>)}
            </div>
            {isGerenteOrLider && (
              <div className="req-detail-field">
                <span className="req-detail-field-label">Entrega Cliente</span>
                {editable('client_delivery_date', <span className="req-detail-field-value">{fmtDate(r.client_delivery_date)}</span>)}
              </div>
            )}
          </div>
        </div>

        {/* ── Progress & hours card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconBarChart />
            <h2>Avance y Horas</h2>
          </div>
          <div className="req-detail-card-body">
            <div className="req-detail-field req-detail-field--full">
              <span className="req-detail-field-label">Porcentaje de Avance</span>
              {editable('progress_percentage', (
                progress !== null ? (
                  <div className="req-detail-progress-wrap">
                    <div className="req-detail-progress-bar">
                      <div
                        className="req-detail-progress-fill"
                        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                      />
                    </div>
                    <span className="req-detail-progress-text">{progress}%</span>
                  </div>
                ) : (
                  <span className="req-detail-empty">Sin registrar</span>
                )
              ))}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Horas Estimadas Dev</span>
              {editable('estimated_completion_hours_dev', (
                <span className="req-detail-field-value req-detail-field-value--mono">
                  {r.estimated_completion_hours_dev != null ? Number(r.estimated_completion_hours_dev).toFixed(1) : '—'}
                </span>
              ))}
            </div>
            {isGerenteOrLider && (
              <div className="req-detail-field">
                <span className="req-detail-field-label">Horas Estimadas Cliente</span>
                {editable('estimated_completion_hours_client', (
                  <span className="req-detail-field-value req-detail-field-value--mono">
                    {r.estimated_completion_hours_client != null ? Number(r.estimated_completion_hours_client).toFixed(1) : '—'}
                  </span>
                ))}
              </div>
            )}
            <div className="req-detail-field">
              <span className="req-detail-field-label">% Facturado</span>
              {editable('billed_percentage', <span className="req-detail-field-value">{r.billed_percentage ?? '—'}</span>)}
            </div>
            {isGerenteOrLider && (
              <div className="req-detail-field">
                <span className="req-detail-field-label">Facturado</span>
                {editable('fully_billed', (
                  <span className={`req-billed-check${r.fully_billed ? ' req-billed-check--on' : ''}`} title={r.fully_billed ? 'Facturado en su totalidad' : 'No facturado'}>
                    {r.fully_billed && <IconCheck />}
                  </span>
                ))}
              </div>
            )}
            {isGerenteOrLider && (
              <div className="req-detail-field">
                <span className="req-detail-field-label">Horas Ext. Autorizadas</span>
                {editable('allow_extended_hours', (
                  <span className={`req-toggle-badge${r.allow_extended_hours ? ' req-toggle-badge--on' : ''}`}>
                    {r.allow_extended_hours ? 'Sí' : 'No'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Documents & extras card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconFileText />
            <h2>Documentos y Extras</h2>
          </div>
          <div className="req-detail-card-body">
            <div className="req-detail-field">
              <span className="req-detail-field-label">Versión EF</span>
              {editable('EF_version', <span className="req-detail-field-value">{r.EF_version ?? '—'}</span>)}
            </div>
            <div className="req-detail-field">
              <span className="req-detail-field-label">Nombre Archivo EF</span>
              {editable('EF_file_name', <span className="req-detail-field-value">{r.EF_file_name ?? '—'}</span>)}
            </div>
            <div className="req-detail-field req-detail-field--full">
              <span className="req-detail-field-label"><IconFolder /> Documentos SharePoint</span>
              {editing && ec?.isGerenteOrLider ? (
                <div className="req-detail-docs-edit">
                  {r.docs_hyperlink && (
                    <a href={r.docs_hyperlink} target="_blank" rel="noopener noreferrer" className="req-detail-doc-link">
                      <IconExtLink /> Abrir carpeta
                    </a>
                  )}
                  <button
                    className="req-fetch-url-btn"
                    disabled={isFetchingUrl}
                    onClick={() => ec.handleFetchUrl(r)}
                  >
                    {isFetchingUrl ? <span className="req-fetch-url-spinner" /> : 'Obtener URL'}
                  </button>
                </div>
              ) : r.docs_hyperlink ? (
                <a href={r.docs_hyperlink} target="_blank" rel="noopener noreferrer" className="req-detail-doc-link">
                  <IconExtLink /> Abrir carpeta
                </a>
              ) : (
                <span className="req-detail-empty">Sin enlace</span>
              )}
            </div>
          </div>
        </div>

        {/* ── Comments card ── */}
        <div className="req-detail-card">
          <div className="req-detail-card-header">
            <IconComment />
            <h2>Comentarios</h2>
          </div>
          <div className="req-detail-card-body req-detail-card-body--single">
            {canEdit('comment') ? (
              editable('comment', (
                r.comment
                  ? <p className="req-detail-comment">{r.comment}</p>
                  : <span className="req-detail-placeholder">Agregar comentario…</span>
              ))
            ) : r.comment ? (
              <p className="req-detail-comment">{r.comment}</p>
            ) : (
              <span className="req-detail-empty">Sin comentarios</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Delete confirmation modal ───────────────────────
   Gerente-only. Deleting a requirement CASCADE-deletes its time_logs,
   requirement_devs and requirement_status_history rows, so deletion is
   reserved for mistake-created requirements: any logged hours, assigned devs
   or child requirements (root_requirement_id → this row) block it — genuine
   cancelled requirements are kept via status change instead. `blockers` is
   null while the related-records check is in flight. The destructive button
   is gated behind a 5-second cooldown, and the SharePoint folder link is
   offered inline because deleting the row does NOT delete the folder. */
function DeleteRequirementModal({ row, blockers, deleting, onClose, onConfirm }) {
  const checking = blockers === null;
  const blocked = !checking && (blockers.timeLogs > 0 || blockers.devs > 0 || blockers.children > 0);
  const [cooldown, setCooldown] = useState(5);

  useEffect(() => {
    if (checking || blocked) return;
    setCooldown(5);
    const iv = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(iv); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [checking, blocked]);

  return (
    <div
      className="req-picker-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !deleting) onClose(); }}
    >
      <div className="req-delete-modal" role="alertdialog" aria-modal="true" aria-labelledby="req-delete-title">
        <div className="req-picker-header">
          <h3 className="req-picker-title req-delete-title" id="req-delete-title">Eliminar Requerimiento</h3>
          <button className="req-picker-close" onClick={onClose} aria-label="Cerrar" disabled={deleting}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="req-delete-body">
          {checking ? (
            <div className="req-delete-checking">
              <span className="req-fetch-url-spinner" />
              <span>Verificando registros relacionados…</span>
            </div>
          ) : blocked ? (
            <>
              <p className="req-delete-text">
                El requerimiento <strong>{row.reference_id}</strong> no se puede eliminar porque tiene registros relacionados:
              </p>
              <ul className="req-delete-blocked-list">
                {blockers.timeLogs > 0 && (
                  <li>{blockers.timeLogs} registro{blockers.timeLogs !== 1 ? 's' : ''} de horas en la bitácora</li>
                )}
                {blockers.devs > 0 && (
                  <li>{blockers.devs} desarrollador{blockers.devs !== 1 ? 'es' : ''} asignado{blockers.devs !== 1 ? 's' : ''}</li>
                )}
                {blockers.children > 0 && (
                  <li>Es raíz de {blockers.children} requerimiento{blockers.children !== 1 ? 's' : ''}</li>
                )}
              </ul>
              <p className="req-delete-text req-delete-text--muted">
                Los requerimientos genuinos que ya no proceden deben conservarse cambiándoles el estatus (p. ej. cancelado).
                Si este requerimiento realmente fue creado por error, primero retira los desarrolladores asignados y las
                referencias raíz desde "Editar Campos" e inténtalo de nuevo.
              </p>
            </>
          ) : (
            <>
              <p className="req-delete-text">
                Estás a punto de eliminar permanentemente el requerimiento <strong>{row.reference_id}</strong>.
                Esta acción no se puede deshacer.
              </p>
              <div className="req-delete-note">
                <p className="req-delete-text">
                  <strong>La carpeta de SharePoint NO se elimina automáticamente</strong> — debes borrarla tú mismo.
                  Se recomienda abrir la carpeta desde aquí, eliminarla en SharePoint y regresar a esta ventana para confirmar.
                </p>
                {row.docs_hyperlink ? (
                  <a
                    href={row.docs_hyperlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="req-delete-sp-link"
                  >
                    <IconFolder />
                    <span>Abrir carpeta de SharePoint</span>
                    <IconExtLink />
                  </a>
                ) : (
                  <p className="req-delete-text req-delete-text--muted">
                    Este requerimiento no tiene un enlace de SharePoint registrado — verifica manualmente si existe una carpeta con el folio <strong>{row.reference_id}</strong>.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="req-delete-footer">
          <button className="req-btn req-btn--secondary" onClick={onClose} disabled={deleting}>
            {blocked ? 'Entendido' : 'Cancelar'}
          </button>
          {!checking && !blocked && (
            <button
              className="req-btn req-delete-confirm"
              onClick={onConfirm}
              disabled={cooldown > 0 || deleting}
            >
              {deleting ? (
                <>
                  <span className="req-fetch-url-spinner" />
                  <span>Eliminando…</span>
                </>
              ) : (
                <>
                  <IconTrash />
                  <span>{cooldown > 0 ? `Eliminar definitivamente (${cooldown})` : 'Eliminar definitivamente'}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Component ───────────────────────────────────── */
function Requirements() {
  /* state */
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('table'); /* 'table' | 'create' | 'detail' | 'duplicate' */
  const [selectedRow, setSelectedRow] = useState(null);
  /* Source requirement for the "Duplicar" flow (full row, prefills the form) */
  const [duplicateSource, setDuplicateSource] = useState(null);
  /* Detail-view edit mode — independent of the table's `editMode` so toggling
     edit inside a folio's full view never leaves the table mid-edit on return */
  const [detailEditMode, setDetailEditMode] = useState(false);

  /* filter state — filterStatus is a multi-select: array of status id strings
     (empty array = no status filter, show all) */
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [filterLeader, setFilterLeader] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterEstimator, setFilterEstimator] = useState('');
  /* filterDevs — multi-select, OR semantics: a requirement matches if AT LEAST
     ONE of the selected devs is assigned to it. Array of user id strings. */
  const [filterDevs, setFilterDevs] = useState([]);
  /* filterBilled — Gerente/Líder only. '' = Todos, 'false' = No facturado,
     'true' = Facturado. Prefilled to 'false' so not-yet-billed requirements
     are shown by default on entry. */
  const [filterBilled, setFilterBilled] = useState('false');
  /* filterBillingType — Gerente/Líder only. '' = Todos, BILLING_TYPE_NONE =
     requirements without a billing type, otherwise a billing_types id string. */
  const [filterBillingType, setFilterBillingType] = useState('');

  /* picker modal state */
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [estimatorModalOpen, setEstimatorModalOpen] = useState(false);
  const [devFilterModalOpen, setDevFilterModalOpen] = useState(false);
  const [billedModalOpen, setBilledModalOpen] = useState(false);
  const [billingTypeModalOpen, setBillingTypeModalOpen] = useState(false);

  /* edit mode */
  const [editMode, setEditMode] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { rowId, field, editType }
  const [editValue, setEditValue] = useState('');
  /* Row id whose full comment is shown in the read-only view modal (opened by
     clicking a clamped Comentarios cell outside edit mode). Kept as an id and
     resolved against `rows` at render time so realtime updates/deletes stay
     reflected while the modal is open. */
  const [commentViewId, setCommentViewId] = useState(null);
  const [fetchingUrls, setFetchingUrls] = useState(new Set());
  const [userId, setUserId] = useState(null);
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);

  /* delete mode (Gerente only) — pick a row, confirm in a warning modal.
     deleteBlockers: null = related-records check in flight;
     { timeLogs, devs, children } once verified. */
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBlockers, setDeleteBlockers] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* dropdown options */
  const [statusOptions, setStatusOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [leaderOptions, setLeaderOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [devPickerOptions, setDevPickerOptions] = useState([]);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  /* ALL billing types (incl. inactive) — the filter must still be able to find
     requirements tagged with a since-deactivated type. The edit picker uses
     only the active subset (activeBillingTypeOptions). */
  const [billingTypeOptions, setBillingTypeOptions] = useState([]);
  const [estimatorOptions, setEstimatorOptions] = useState([]);

  /* dev picker selection (array of user id strings) */
  const [devSelection, setDevSelection] = useState([]);

  /* ── Fetch user role ── */
  useEffect(() => {
    async function fetchRole() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);
      const { data } = await supabase
        .from('users')
        .select('roles(name)')
        .eq('id', user.id)
        .single();
      const roleName = data?.roles?.name ?? null;
      setUserRole(roleName);
      /* Líderes start with the Líder filter pre-set to themselves */
      if (roleName === 'Líder') setFilterLeader(String(user.id));
    }
    fetchRole();
  }, []);

  /* ── Fetch filter options ── */
  useEffect(() => {
    async function fetchOptions() {
      const [statusRes, clientRes, leaderRes, projectRes, specRes, billingTypeRes] = await Promise.all([
        supabase.from('status').select('id, name, color').order('name'),
        supabase.from('clients').select('id, name').eq('active', true).order('name'),
        supabase.from('users').select('id, full_name, roles(name)').eq('active', true).order('full_name'),
        supabase.from('projects').select('id, name, client_id').eq('active', true).order('name'),
        supabase.from('specializations').select('id, name').order('name'),
        supabase.from('billing_types').select('id, name, active').order('name'),
      ]);
      setStatusOptions(statusRes.data || []);
      setClientOptions(clientRes.data || []);
      /* leaders = Gerente + Líder */
      const allUsers = leaderRes.data || [];
      const leaders = allUsers.filter((u) => u.roles?.name === 'Gerente' || u.roles?.name === 'Líder');
      setLeaderOptions(leaders);
      /* dev picker = Líder + Desarrollador (assignable users) */
      const assignable = allUsers.filter((u) => u.roles?.name === 'Líder' || u.roles?.name === 'Desarrollador');
      setDevPickerOptions(assignable);
      setProjectOptions(projectRes.data || []);
      setSpecializationOptions(specRes.data || []);
      setBillingTypeOptions(billingTypeRes.data || []);
      setEstimatorOptions(allUsers);
    }
    fetchOptions();
  }, []);

  /* ── Fetch requirements ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('requirements')
      .select(REQUIREMENTS_SELECT)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching requirements:', error);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Realtime: refs ── */

  /* Always-current mirror of editingCell — avoids stale closures inside the
     subscription callback which is set up once and never re-created. */
  const editingCellRef = useRef(null);
  useEffect(() => { editingCellRef.current = editingCell; }, [editingCell]);

  /* Supabase channel handle (for cleanup on unmount) */
  const channelRef = useRef(null);

  /* Per-row debounce timers — collapses bursts of events on the same row
     (e.g. 3 requirement_devs INSERTs for one assignment save) into a single
     re-fetch that fires after the dust settles. */
  const pendingRefetchRef = useRef(new Map());

  /* Tracks whether we have ever connected, so we can distinguish a
     first-time SUBSCRIBED from a reconnect after a drop. */
  const wasConnectedRef = useRef(false);

  /* Rows whose realtime events were skipped because the user was editing
     them. If the user saves, the optimistic lock resolves the conflict; if
     they cancel, nothing would ever apply the missed update — so these are
     re-fetched when editing ends (see effect below the subscription). */
  const skippedWhileEditingRef = useRef(new Set());

  /* ── Realtime: re-fetch a single row with all joins ── */
  const refetchRow = useCallback(async (rowId) => {
    const { data } = await supabase
      .from('requirements')
      .select(REQUIREMENTS_SELECT)
      .eq('id', rowId)
      .maybeSingle();

    if (!data) return; // row was deleted or RLS denies access

    setRows((prev) => {
      const exists = prev.some((r) => r.id === rowId);
      if (exists) return prev.map((r) => (r.id === rowId ? data : r));
      /* INSERT from another user — prepend so newest-first order is preserved */
      return [data, ...prev];
    });
  }, []);

  /* ── Realtime: debounced re-fetch per row ── */
  const scheduleRefetch = useCallback((rowId) => {
    const pending = pendingRefetchRef.current;
    if (pending.has(rowId)) clearTimeout(pending.get(rowId));
    const timer = setTimeout(() => {
      pending.delete(rowId);
      refetchRow(rowId);
    }, 150);
    pending.set(rowId, timer);
  }, [refetchRow]);

  /* ── Realtime: subscription ── */
  useEffect(() => {
    const channel = supabase
      .channel('requirements-live')

      /* ── requirements table events ── */
      .on('postgres_changes', { event: '*', schema: 'public', table: 'requirements' }, (payload) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const rowId = eventType === 'DELETE' ? oldRow.id : newRow.id;

        if (eventType === 'DELETE') {
          /* If the user has a modal open on this row, close it with a notice */
          if (editingCellRef.current?.rowId === rowId) {
            setEditingCell(null);
            setEditValue('');
            setDevSelection([]);
            setToast({ type: 'error', message: 'Este requerimiento fue eliminado por otro usuario' });
          }
          /* If the user has the delete modal open on this row, close it */
          setDeleteTarget((prev) => {
            if (prev?.id === rowId) {
              setDeleteBlockers(null);
              setToast({ type: 'error', message: 'Este requerimiento fue eliminado por otro usuario' });
              return null;
            }
            return prev;
          });
          /* If the user is viewing this row's detail, kick them back to table */
          setSelectedRow((prev) => {
            if (prev?.id === rowId) {
              setView('table');
              setDetailEditMode(false);
              setToast({ type: 'error', message: 'Este requerimiento fue eliminado por otro usuario' });
              return null;
            }
            return prev;
          });
          /* Cancel any pending refetch for this row */
          if (pendingRefetchRef.current.has(rowId)) {
            clearTimeout(pendingRefetchRef.current.get(rowId));
            pendingRefetchRef.current.delete(rowId);
          }
          skippedWhileEditingRef.current.delete(rowId);
          setRows((prev) => prev.filter((r) => r.id !== rowId));
          return;
        }

        /* UPDATE: skip if the user is actively editing this row — their
           in-flight save + optimistic lock handles the conflict already */
        if (eventType === 'UPDATE' && editingCellRef.current?.rowId === rowId) {
          skippedWhileEditingRef.current.add(rowId);
          return;
        }

        scheduleRefetch(rowId);
      })

      /* ── requirement_devs table events ── */
      /* REPLICA IDENTITY FULL is set on this table so old record always
         carries requirement_id even for DELETEs */
      .on('postgres_changes', { event: '*', schema: 'public', table: 'requirement_devs' }, (payload) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        const requirementId = eventType === 'DELETE' ? oldRow.requirement_id : newRow.requirement_id;
        if (!requirementId) return;

        /* Skip if the user is editing devs for this exact row — their own
           saveDevs handler already applied the optimistic update */
        if (
          editingCellRef.current?.rowId === requirementId &&
          editingCellRef.current?.editType === 'devs'
        ) {
          skippedWhileEditingRef.current.add(requirementId);
          return;
        }

        scheduleRefetch(requirementId);
      })

      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          if (wasConnectedRef.current) {
            /* Reconnected after a drop — events were missed, do a full re-fetch */
            fetchData();
          }
          wasConnectedRef.current = true;
        }
      });

    channelRef.current = channel;

    return () => {
      /* Clear all debounce timers so no stale re-fetches fire after unmount */
      pendingRefetchRef.current.forEach((timer) => clearTimeout(timer));
      pendingRefetchRef.current.clear();
      wasConnectedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [fetchData, scheduleRefetch]); // both are stable useCallbacks

  /* ── Re-fetch rows whose realtime events were skipped during an edit ──
     Runs when editing ends (save or cancel). After a save the refetch is a
     harmless refresh; after a cancel it applies the missed remote update. */
  useEffect(() => {
    if (editingCell) return;
    const skipped = skippedWhileEditingRef.current;
    if (skipped.size === 0) return;
    skipped.forEach((id) => scheduleRefetch(id));
    skipped.clear();
  }, [editingCell, scheduleRefetch]);

  /* reset page on filter/search change */
  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterClient, filterLeader, filterProject, filterEstimator, filterDevs, filterBilled, filterBillingType]);

  /* ── Filtered projects based on selected client ── */
  const filteredProjectOptions = useMemo(() => {
    if (!filterClient) return projectOptions;
    return projectOptions.filter((p) => String(p.client_id) === String(filterClient));
  }, [filterClient, projectOptions]);

  /* Reset project filter when client changes and selected project no longer matches */
  useEffect(() => {
    if (filterClient && filterProject) {
      const match = filteredProjectOptions.find((p) => String(p.id) === String(filterProject));
      if (!match) setFilterProject('');
    }
  }, [filterClient, filterProject, filteredProjectOptions]);

  /* Handle project selection — auto-fill client */
  const handleProjectSelect = useCallback((projectId) => {
    setFilterProject(projectId ? String(projectId) : '');
    if (projectId) {
      const proj = projectOptions.find((p) => String(p.id) === String(projectId));
      if (proj) setFilterClient(String(proj.client_id));
    }
  }, [projectOptions]);

  /* Selected label helpers */
  const selectedStatusLabel = useMemo(() => {
    if (filterStatus.length === 0) return 'Estatus';
    if (filterStatus.length === 1) {
      const s = statusOptions.find((o) => String(o.id) === String(filterStatus[0]));
      return s ? s.name : '1 estatus';
    }
    return `${filterStatus.length} estatus`;
  }, [filterStatus, statusOptions]);

  const selectedClientLabel = useMemo(() => {
    if (!filterClient) return 'Cliente';
    const c = clientOptions.find((o) => String(o.id) === String(filterClient));
    return c ? c.name : 'Cliente';
  }, [filterClient, clientOptions]);

  const selectedProjectLabel = useMemo(() => {
    if (!filterProject) return 'Proyecto';
    const p = projectOptions.find((o) => String(o.id) === String(filterProject));
    return p ? p.name : 'Proyecto';
  }, [filterProject, projectOptions]);

  const selectedEstimatorLabel = useMemo(() => {
    if (!filterEstimator) return 'Estimador';
    const u = estimatorOptions.find((o) => String(o.id) === String(filterEstimator));
    return u ? u.full_name : 'Estimador';
  }, [filterEstimator, estimatorOptions]);

  const selectedDevsLabel = useMemo(() => {
    if (filterDevs.length === 0) return 'Desarrolladores';
    if (filterDevs.length === 1) {
      const u = devPickerOptions.find((o) => String(o.id) === String(filterDevs[0]));
      return u ? u.full_name : '1 desarrollador';
    }
    return `${filterDevs.length} desarrolladores`;
  }, [filterDevs, devPickerOptions]);

  /* Edit picker: active types only (inactive catalog values never offered) */
  const activeBillingTypeOptions = useMemo(
    () => billingTypeOptions.filter((b) => b.active),
    [billingTypeOptions]
  );

  /* Filter picker: "sin tipo" sentinel + every type, inactive ones labelled */
  const billingTypeFilterOptions = useMemo(
    () => [
      { id: BILLING_TYPE_NONE, name: 'Sin tipo de facturación' },
      ...billingTypeOptions.map((b) => ({ id: b.id, name: b.active ? b.name : `${b.name} (inactivo)` })),
    ],
    [billingTypeOptions]
  );

  const selectedBillingTypeLabel = useMemo(() => {
    if (!filterBillingType) return 'Tipo de Facturación';
    const b = billingTypeFilterOptions.find((o) => String(o.id) === String(filterBillingType));
    return b ? b.name : 'Tipo de Facturación';
  }, [filterBillingType, billingTypeFilterOptions]);

  const selectedBilledLabel = filterBilled === 'true'
    ? 'Facturados'
    : filterBilled === 'false'
      ? 'No facturados'
      : 'Facturado';

  /* ── Filtering ── */
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      /* search by folio */
      if (search.trim()) {
        const s = search.toLowerCase();
        const folio = (row.reference_id || '').toLowerCase();
        if (!folio.includes(s)) return false;
      }
      /* dropdown filters */
      if (filterStatus.length > 0 && !filterStatus.includes(String(row.status_id))) return false;
      if (filterClient && String(row.project?.client_id) !== String(filterClient)) return false;
      if (filterLeader && String(row.leader_user_id) !== String(filterLeader)) return false;
      if (filterProject && String(row.project_id) !== String(filterProject)) return false;
      if (filterEstimator && String(row.estimator_user_id) !== String(filterEstimator)) return false;
      /* devs filter — OR semantics: keep the row if ANY assigned dev is selected */
      if (filterDevs.length > 0) {
        const assigned = row.requirement_devs || [];
        if (!assigned.some((d) => filterDevs.includes(String(d.user_id)))) return false;
      }
      /* billed filter — Gerente/Líder only (Desarrolladores can't see the column) */
      if ((userRole === 'Gerente' || userRole === 'Líder') && filterBilled) {
        if (Boolean(row.fully_billed) !== (filterBilled === 'true')) return false;
      }
      /* billing type filter — Gerente/Líder only (column hidden from Desarrolladores) */
      if ((userRole === 'Gerente' || userRole === 'Líder') && filterBillingType) {
        if (filterBillingType === BILLING_TYPE_NONE) {
          if (row.billing_type_id != null) return false;
        } else if (String(row.billing_type_id) !== String(filterBillingType)) {
          return false;
        }
      }
      return true;
    });
  }, [rows, search, filterStatus, filterClient, filterLeader, filterProject, filterEstimator, filterDevs, filterBilled, filterBillingType, userRole]);

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageList = getPageList(safePage, totalPages);

  const billedFilterActive = (userRole === 'Gerente' || userRole === 'Líder') && !!filterBilled;
  const billingTypeFilterActive = (userRole === 'Gerente' || userRole === 'Líder') && !!filterBillingType;
  const hasActiveFilters = filterStatus.length > 0 || filterClient || filterLeader || filterProject || filterEstimator || filterDevs.length > 0 || billedFilterActive || billingTypeFilterActive;

  /* ── Root picker options: original requirements only (no root of their own) ── */
  const rootOptions = useMemo(
    () => rows.filter((r) => r.root_requirement_id === null),
    [rows]
  );

  /* ── id → reference_id map for resolving root labels client-side ── */
  const rootRefMap = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, r.reference_id])),
    [rows]
  );

  function clearAllFilters() {
    setFilterStatus([]);
    setFilterClient('');
    setFilterLeader('');
    setFilterProject('');
    setFilterEstimator('');
    setFilterDevs([]);
    setFilterBilled('');
    setFilterBillingType('');
  }

  /* ── Edit-mode hover guides (row + column crosshair) ──
     Toggles .req-hl directly on the DOM via delegated events instead of React
     state — re-rendering ~500 cells on every hovered-cell change would be
     wasteful. Stale classes are cleared by the effect below. */
  const tableRef = useRef(null);
  const hoverHl = useRef({ td: null, cells: [] });

  const clearHoverHl = useCallback(() => {
    const s = hoverHl.current;
    if (!s.td && s.cells.length === 0) return;
    s.cells.forEach((el) => el.classList.remove('req-hl'));
    s.cells = [];
    s.td = null;
  }, []);

  const handleTableHover = useCallback((e) => {
    const s = hoverHl.current;
    const td = e.target.closest('td');
    if (td === s.td) return; /* still inside the same cell — nothing to do */
    clearHoverHl();
    const table = tableRef.current;
    /* guides only make sense over a cell the user can actually edit */
    if (!td || !table || !table.contains(td) || !td.classList.contains('req-td--editable')) return;
    const tr = td.parentElement;
    const colIdx = td.cellIndex;
    const cells = [];
    /* row guide: frozen folio cell → hovered cell */
    for (let i = 0; i <= colIdx; i++) cells.push(tr.cells[i]);
    /* column guide: header → hovered cell */
    const th = table.tHead?.rows[0]?.cells[colIdx];
    if (th) cells.push(th);
    const bodyRows = tr.parentElement.rows;
    for (let r = 0; r < tr.sectionRowIndex; r++) {
      const cell = bodyRows[r].cells[colIdx];
      if (cell) cells.push(cell);
    }
    cells.forEach((el) => el.classList.add('req-hl'));
    s.td = td;
    s.cells = cells;
  }, [clearHoverHl]);

  /* Clear guides when leaving edit mode or when the visible rows change
     (pagination, filters, realtime refetch) — the highlighted cells may no
     longer match what's under the cursor. */
  useEffect(() => {
    clearHoverHl();
  }, [editMode, paginatedRows, clearHoverHl]);

  /* ── Edit mode handlers ── */
  const showToast = useCallback((type, message) => setToast({ type, message }), []);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
    setDevSelection([]);
  }, []);

  const handleFetchUrl = useCallback(async (row) => {
    setFetchingUrls((prev) => new Set([...prev, row.id]));
    try {
      const { error } = await supabase.functions.invoke('create-requirement-folder', {
        body: {
          reference_id: row.reference_id,
          requirement_id: row.id,
          estimator_user_id: null,
          leader_user_id: row.leader_user_id,
        },
      });
      if (error) throw error;
      showToast('success', 'URL de SharePoint actualizada');
      fetchData();
    } catch {
      showToast('error', 'No se pudo obtener la URL de SharePoint');
    } finally {
      setFetchingUrls((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }, [showToast, fetchData]);

  const startEditing = useCallback((row, col) => {
    if (!col.editType) return;
    if (col.editType === 'devs') {
      const currentIds = (row.requirement_devs || []).map((d) => String(d.user_id));
      setDevSelection(currentIds);
      setEditingCell({ rowId: row.id, field: col.field, editType: 'devs' });
      return;
    }
    if (col.editType === 'root') {
      setEditingCell({ rowId: row.id, field: col.field, editType: 'root' });
      return;
    }
    let val = row[col.field];
    if (val == null) val = '';
    else val = String(val);
    setEditingCell({ rowId: row.id, field: col.field, editType: col.editType });
    setEditValue(val);
  }, []);

  const saveCell = useCallback(async (rowId, field, newValue, editType) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const oldValue = row[field];
    if (String(oldValue ?? '') === String(newValue ?? '')) {
      cancelEditing();
      return;
    }

    /* optimistic update — also update the joined relation for status fields */
    setRows((prev) => prev.map((r) => {
      if (r.id !== rowId) return r;
      const patch = { ...r, [field]: newValue };
      if (field === 'status_id' && statusOptions.length) {
        const found = statusOptions.find((s) => String(s.id) === String(newValue));
        patch.status_rel = found ? { id: found.id, name: found.name, color: found.color } : null;
      }
      if (field === 'status_client_id' && statusOptions.length) {
        const found = statusOptions.find((s) => String(s.id) === String(newValue));
        patch.status_client_rel = found ? { id: found.id, name: found.name, color: found.color } : null;
      }
      if (field === 'specialization_id' && specializationOptions.length) {
        const found = specializationOptions.find((s) => String(s.id) === String(newValue));
        patch.specialization = found ? { id: found.id, name: found.name } : null;
      }
      if (field === 'billing_type_id') {
        const found = billingTypeOptions.find((b) => String(b.id) === String(newValue));
        patch.billing_type = found ? { id: found.id, name: found.name } : null;
      }
      return patch;
    }));
    cancelEditing();

    const { data, error } = await supabase
      .from('requirements')
      .update({ [field]: newValue })
      .eq('id', rowId)
      .eq('updated_at', row.updated_at)
      .select('updated_at')
      .single();

    if (error || !data) {
      /* rollback */
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, [field]: oldValue } : r)));
      showToast('error', 'El registro fue modificado por otro usuario. Actualizando…');
      fetchData();
      return;
    }

    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, updated_at: data.updated_at } : r)));

    /* status change audit trail is handled by DB trigger (trg_log_status_change) */

    /* fire-and-forget notification for internal status changes */
    if (field === 'status_id') {
      const oldStatusName = row.status_rel?.name || '';
      const newStatusName = statusOptions.find((s) => String(s.id) === String(newValue))?.name || '';
      supabase.functions.invoke('notify-internal-status-change', {
        body: { requirement_id: rowId, old_status_name: oldStatusName, new_status_name: newStatusName },
      }).catch(() => {});
    }

    /* fire-and-forget notification for client status changes */
    if (field === 'status_client_id') {
      const oldStatusName = row.status_client_rel?.name || '';
      const newStatusName = statusOptions.find((s) => String(s.id) === String(newValue))?.name || '';
      supabase.functions.invoke('notify-client-status-change', {
        body: { requirement_id: rowId, old_status_name: oldStatusName, new_status_name: newStatusName },
      }).catch(() => {});
    }

    /* fire-and-forget notification for comment updates */
    if (field === 'comment') {
      supabase.functions.invoke('notify-comment-update', {
        body: { requirement_id: rowId, old_comment: oldValue ?? null, new_comment: newValue ?? null },
      }).catch(() => {});
    }

    showToast('success', 'Guardado correctamente');
  }, [rows, userId, statusOptions, specializationOptions, billingTypeOptions, cancelEditing, showToast, fetchData]);

  const commitEdit = useCallback((row, col) => {
    let val = editValue;

    if (col.editType === 'number') {
      val = val === '' ? null : parseFloat(val);
      if (val !== null && isNaN(val)) { cancelEditing(); return; }
      if (val !== null && col.editConfig?.min != null && val < col.editConfig.min) val = col.editConfig.min;
      if (val !== null) val = Math.round(val * 100) / 100;
      if (val !== null && col.editConfig?.halfHourOnly && Math.abs(val * 2 - Math.round(val * 2)) > 1e-9) {
        showToast('error', 'Las horas deben ser múltiplos de 0.5 (ej. 10.0, 10.5, 11.0)');
        cancelEditing();
        return;
      }
    } else if (col.editType === 'progress') {
      val = val.replace('%', '').trim();
      val = val === '' ? null : parseInt(val, 10);
      if (val !== null && isNaN(val)) { cancelEditing(); return; }
      if (val !== null) val = Math.max(0, Math.min(100, val));
    } else if (col.editType === 'text') {
      val = val.trim() || null;
    }

    saveCell(row.id, col.field, val, col.editType);
  }, [editValue, saveCell, cancelEditing, showToast]);

  const saveDevs = useCallback(async (rowId, newUserIds) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const currentIds = (row.requirement_devs || []).map((d) => String(d.user_id));
    const newIds = newUserIds.map(String);
    const toDelete = currentIds.filter((id) => !newIds.includes(id));
    const toInsert = newIds.filter((id) => !currentIds.includes(id));

    if (toDelete.length === 0 && toInsert.length === 0) {
      cancelEditing();
      return;
    }

    /* optimistic update */
    const optimisticDevs = newIds.map((uid) => {
      const existing = (row.requirement_devs || []).find((d) => String(d.user_id) === uid);
      if (existing) return existing;
      const userInfo = devPickerOptions.find((u) => String(u.id) === uid);
      return { user_id: uid, user: userInfo ? { id: userInfo.id, full_name: userInfo.full_name, roles: userInfo.roles } : null };
    });
    setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, requirement_devs: optimisticDevs }));
    cancelEditing();

    try {
      if (toDelete.length > 0) {
        const { error } = await supabase
          .from('requirement_devs')
          .delete()
          .eq('requirement_id', rowId)
          .in('user_id', toDelete);
        if (error) throw error;
      }
      if (toInsert.length > 0) {
        const { error } = await supabase
          .from('requirement_devs')
          .insert(toInsert.map((uid) => ({ requirement_id: rowId, user_id: uid })));
        if (error) throw error;
      }
      showToast('success', 'Desarrolladores actualizados');

      /* fire-and-forget notification for newly added devs only */
      if (toInsert.length > 0) {
        supabase.functions.invoke('notify-devs-assigned', {
          body: { requirement_id: rowId, new_user_ids: toInsert },
        }).catch(() => {});
      }
    } catch {
      /* rollback */
      setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, requirement_devs: row.requirement_devs }));
      showToast('error', 'Error al actualizar los desarrolladores');
    }
  }, [rows, devPickerOptions, cancelEditing, showToast]);

  const saveRoot = useCallback(async (rowId, newRootId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const oldRootId = row.root_requirement_id ?? null;
    if (oldRootId === newRootId) { cancelEditing(); return; }

    /* optimistic update — rootRefMap derives the label, so only patch the FK */
    setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, root_requirement_id: newRootId }));
    cancelEditing();

    const { data, error } = await supabase
      .from('requirements')
      .update({ root_requirement_id: newRootId })
      .eq('id', rowId)
      .eq('updated_at', row.updated_at)
      .select('updated_at')
      .single();

    if (error || !data) {
      /* rollback */
      setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, root_requirement_id: oldRootId }));
      showToast('error', 'El registro fue modificado por otro usuario. Actualizando…');
      fetchData();
      return;
    }
    setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, updated_at: data.updated_at }));
    showToast('success', 'Raíz actualizada correctamente');
  }, [rows, cancelEditing, showToast, fetchData]);

  const saveEstimator = useCallback(async (rowId, newEstimatorId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const oldEstimatorId = row.estimator_user_id ?? null;
    const newId = newEstimatorId || null;
    if (String(oldEstimatorId ?? '') === String(newId ?? '')) { cancelEditing(); return; }

    /* optimistic update */
    const found = newId ? estimatorOptions.find((u) => String(u.id) === String(newId)) : null;
    setRows((prev) => prev.map((r) => r.id !== rowId ? r : {
      ...r,
      estimator_user_id: newId,
      estimator: found ? { id: found.id, full_name: found.full_name } : null,
    }));
    cancelEditing();

    const { data, error } = await supabase
      .from('requirements')
      .update({ estimator_user_id: newId })
      .eq('id', rowId)
      .eq('updated_at', row.updated_at)
      .select('updated_at')
      .single();

    if (error || !data) {
      /* rollback */
      setRows((prev) => prev.map((r) => r.id !== rowId ? r : {
        ...r, estimator_user_id: oldEstimatorId, estimator: row.estimator,
      }));
      showToast('error', 'El registro fue modificado por otro usuario. Actualizando…');
      fetchData();
      return;
    }

    setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, updated_at: data.updated_at }));
    showToast('success', 'Estimador actualizado correctamente');

    /* fire-and-forget notification only when a new estimator is assigned */
    if (newId) {
      supabase.functions.invoke('notify-estimator-assigned', {
        body: { requirement_id: rowId, estimator_user_id: newId },
      }).catch(() => {});
    }
  }, [rows, estimatorOptions, cancelEditing, showToast, fetchData]);

  const saveLider = useCallback(async (rowId, newLiderId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    const oldLiderId = row.leader_user_id ?? null;
    const newId = newLiderId || null;
    if (String(oldLiderId ?? '') === String(newId ?? '')) { cancelEditing(); return; }

    /* optimistic update */
    const found = newId ? leaderOptions.find((u) => String(u.id) === String(newId)) : null;
    setRows((prev) => prev.map((r) => r.id !== rowId ? r : {
      ...r,
      leader_user_id: newId,
      leader: found ? { id: found.id, full_name: found.full_name } : null,
    }));
    cancelEditing();

    const { data, error } = await supabase
      .from('requirements')
      .update({ leader_user_id: newId })
      .eq('id', rowId)
      .eq('updated_at', row.updated_at)
      .select('updated_at')
      .single();

    if (error || !data) {
      /* rollback */
      setRows((prev) => prev.map((r) => r.id !== rowId ? r : {
        ...r, leader_user_id: oldLiderId, leader: row.leader,
      }));
      showToast('error', 'El registro fue modificado por otro usuario. Actualizando…');
      fetchData();
      return;
    }

    setRows((prev) => prev.map((r) => r.id !== rowId ? r : { ...r, updated_at: data.updated_at }));
    showToast('success', 'Líder actualizado correctamente');
  }, [rows, leaderOptions, cancelEditing, showToast, fetchData]);

  /* ── Role checks ── */
  const isGerente = userRole === 'Gerente';
  const isGerenteOrLider = userRole === 'Gerente' || userRole === 'Líder';
  const isDesarrollador = userRole === 'Desarrollador';
  const canEditFields = isGerenteOrLider || isDesarrollador;
  /* Desarrollador may only modify these three columns (also enforced by DB trigger trg_enforce_developer_update_columns) */
  const DEV_ALLOWED_FIELDS = useMemo(() => new Set(['progress_percentage', 'status_id', 'comment']), []);

  /* A Desarrollador can now SEE requirements where they are the estimator (RLS
     `requirements_select`), but the UPDATE policy still only permits rows where
     they are assigned as a developer. So a Desarrollador may edit a row only
     when assigned as a dev — estimator-only rows are read-only for them.
     Gerente/Líder are unaffected (their own permission gating lives elsewhere). */
  const canDevEditRow = useCallback((row) => {
    if (!isDesarrollador) return true;
    return (row?.requirement_devs || []).some((d) => String(d.user_id) === String(userId));
  }, [isDesarrollador, userId]);

  /* ── Delete mode handlers (Gerente only) ──
     Deleting cascades to time_logs / requirement_devs / status history, so any
     of those (or a child requirement referencing this one as root) blocks the
     deletion — only mistake-created, empty requirements may be removed. */
  const openDeleteModal = useCallback(async (row) => {
    setDeleteTarget(row);
    setDeleteBlockers(null);
    const children = rows.filter((r) => r.root_requirement_id === row.id).length;
    const { count, error } = await supabase
      .from('time_logs')
      .select('id', { count: 'exact', head: true })
      .eq('requirement_id', row.id);
    if (error) {
      setDeleteTarget(null);
      showToast('error', 'No se pudieron verificar las horas registradas. Intenta de nuevo.');
      return;
    }
    setDeleteBlockers({ timeLogs: count ?? 0, devs: (row.requirement_devs || []).length, children });
  }, [rows, showToast]);

  const closeDeleteModal = useCallback(() => {
    setDeleteTarget(null);
    setDeleteBlockers(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      /* Re-verify right before deleting — hours/devs may have appeared while
         the modal was open (the SharePoint round-trip takes a while) */
      const { count, error: countError } = await supabase
        .from('time_logs')
        .select('id', { count: 'exact', head: true })
        .eq('requirement_id', deleteTarget.id);
      if (countError) throw countError;
      const liveRow = rows.find((r) => r.id === deleteTarget.id);
      const devs = (liveRow?.requirement_devs || []).length;
      const children = rows.filter((r) => r.root_requirement_id === deleteTarget.id).length;
      if ((count ?? 0) > 0 || devs > 0 || children > 0) {
        setDeleteBlockers({ timeLogs: count ?? 0, devs, children });
        return;
      }

      const { data, error } = await supabase
        .from('requirements')
        .delete()
        .eq('id', deleteTarget.id)
        .select('id');
      if (error) throw error;
      /* RLS silently filters unauthorized deletes — treat 0 rows as failure */
      if (!data || data.length === 0) throw new Error('delete blocked');

      setRows((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      showToast('success', `Requerimiento ${deleteTarget.reference_id} eliminado correctamente`);
      setDeleteTarget(null);
      setDeleteBlockers(null);
      setDeleteMode(false);
    } catch {
      showToast('error', 'No se pudo eliminar el requerimiento. Intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, rows, showToast]);

  /* ── Column definitions ── */
  const columns = useMemo(() => {
    const cols = [
      {
        key: 'reference_id',
        label: 'ID Folio',
        render: (r) => r.reference_id ? (
          <button
            className="req-folio-link"
            onClick={(e) => {
              e.stopPropagation();
              if (deleteMode) { openDeleteModal(r); return; }
              if (!editMode) { setSelectedRow(r); setDetailEditMode(false); cancelEditing(); setView('detail'); }
            }}
            tabIndex={editMode ? -1 : 0}
          >
            {r.reference_id}
          </button>
        ) : '—',
      },
      { key: 'client', label: 'Cliente', render: (r) => r.project?.client?.name ?? '—' },
      { key: 'project', label: 'Proyecto', render: (r) => r.project?.name ?? '—' },
      {
        key: 'root',
        label: 'Root',
        ...(isGerenteOrLider ? { field: 'root_requirement_id', editType: 'root' } : {}),
        render: (r) => r.root_requirement_id ? (rootRefMap[r.root_requirement_id] ?? '—') : '—',
      },
      { key: 'description', label: 'Descripción', field: 'description', editType: 'text', editConfig: { maxLength: 500 }, render: (r) => r.description ? <div className="req-cell-wrap">{r.description}</div> : '—', wide: true },
      { key: 'hours_dev', label: 'Horas Estimadas Dev', field: 'estimated_completion_hours_dev', editType: 'number', editConfig: { min: 0, step: '0.5', halfHourOnly: true }, render: (r) => r.estimated_completion_hours_dev != null ? Number(r.estimated_completion_hours_dev).toFixed(1) : '—' },
      {
        key: 'allow_extended_hours',
        label: 'Ext. Autorizadas',
        ...(isGerenteOrLider ? { field: 'allow_extended_hours', editType: 'toggle' } : {}),
        render: (r) => (
          <span className={`req-toggle-badge${r.allow_extended_hours ? ' req-toggle-badge--on' : ''}`}>
            {r.allow_extended_hours ? 'Sí' : 'No'}
          </span>
        ),
      },
    ];

    if (isGerenteOrLider) {
      cols.push({ key: 'hours_client', label: 'Horas Estimadas Cliente', field: 'estimated_completion_hours_client', editType: 'number', editConfig: { min: 0, step: '0.5', halfHourOnly: true }, render: (r) => r.estimated_completion_hours_client != null ? Number(r.estimated_completion_hours_client).toFixed(1) : '—' });
    }

    cols.push(
      {
        key: 'specialization',
        label: 'Especialidad',
        ...(isGerenteOrLider ? { field: 'specialization_id', editType: 'specialization' } : {}),
        render: (r) => r.specialization?.name ?? '—',
      },
      { key: 'requirement_type', label: 'Tipo de Requerimiento', field: 'requirement_type', editType: 'text', editConfig: { maxLength: 30 }, render: (r) => r.requirement_type ?? '—' },
      /* Tipo de Facturación — Gerente/Líder only: hidden entirely from Desarrolladores
         (write also blocked server-side by enforce_developer_requirement_update_columns). */
      ...(isGerenteOrLider ? [{
        key: 'billing_type',
        label: 'Tipo de Facturación',
        field: 'billing_type_id',
        editType: 'billing_type',
        render: (r) => r.billing_type?.name ?? '—',
      }] : []),
      {
        key: 'status',
        label: 'Estatus',
        field: 'status_id',
        editType: 'status',
        render: (r) => {
          const s = r.status_rel;
          if (!s) return '—';
          return (
            <span className="req-status-badge" style={{ backgroundColor: s.color, color: getContrastColor(s.color) }}>
              {s.name}
            </span>
          );
        },
      },
    );

    if (isGerenteOrLider) {
      cols.push({
        key: 'status_client',
        label: 'Estatus Cliente',
        field: 'status_client_id',
        editType: 'status',
        render: (r) => {
          const s = r.status_client_rel;
          if (!s) return '—';
          return (
            <span className="req-status-badge" style={{ backgroundColor: s.color, color: getContrastColor(s.color) }}>
              {s.name}
            </span>
          );
        },
      });
    }

    cols.push(
      { key: 'created_at', label: 'Fecha Creación Folio', render: (r) => formatDate(r.created_at) },
      { key: 'VoBo_estimated_date', label: 'Fecha Estimación VoBo', field: 'VoBo_estimated_date', editType: 'date', render: (r) => formatDate(r.VoBo_estimated_date) },
      { key: 'construction_start_date', label: 'Fecha Inicio Construcción', field: 'construction_start_date', editType: 'date', render: (r) => formatDate(r.construction_start_date) },
      { key: 'dev_delivery_date', label: 'Fecha Entrega Desarrollador', field: 'dev_delivery_date', editType: 'date', render: (r) => formatDate(r.dev_delivery_date) },
    );

    if (isGerenteOrLider) {
      cols.push({ key: 'client_delivery_date', label: 'Fecha Entrega Cliente', field: 'client_delivery_date', editType: 'date', render: (r) => formatDate(r.client_delivery_date) });
    }

    cols.push(
      { key: 'progress', label: '% Avance', field: 'progress_percentage', editType: 'progress', editConfig: { min: 0, max: 100 }, render: (r) => r.progress_percentage != null ? `${r.progress_percentage}%` : '—' },
      {
        key: 'estimator',
        label: 'Estimador',
        ...(isGerenteOrLider ? { field: 'estimator_user_id', editType: 'estimator' } : {}),
        render: (r) => r.estimator?.full_name ?? '—',
      },
      {
        key: 'leader',
        label: 'Líder',
        ...(isGerenteOrLider ? { field: 'leader_user_id', editType: 'lider' } : {}),
        render: (r) => r.leader?.full_name ?? '—',
      },
      {
        key: 'devs',
        label: 'Desarrolladores',
        field: 'requirement_devs',
        editType: 'devs',
        render: (r) => {
          const devs = (r.requirement_devs || []).filter((d) => d.user?.full_name);
          if (devs.length === 0) return '—';
          return (
            <div className="req-devs-pills">
              {devs.map((d, i) => {
                const roleName = d.user?.roles?.name;
                const isLider = roleName === 'Líder' || roleName === 'Gerente';
                return (
                  <span key={d.user_id ?? i} className={`req-dev-pill${isLider ? ' req-dev-pill--lider' : ' req-dev-pill--dev'}`}>
                    {d.user.full_name}
                  </span>
                );
              })}
            </div>
          );
        },
        wide: true,
      },
      { key: 'functional', label: 'Funcional', field: 'functional_user_name', editType: 'text', editConfig: { maxLength: 30 }, render: (r) => r.functional_user_name ?? '—' },
      { key: 'module', label: 'Módulo', field: 'module', editType: 'text', editConfig: { maxLength: 10 }, render: (r) => r.module ?? '—' },
      { key: 'ef_version', label: 'Versión EF', field: 'EF_version', editType: 'text', editConfig: { maxLength: 10 }, render: (r) => r.EF_version ?? '—' },
      { key: 'ef_file', label: 'EF Nombre Archivo', field: 'EF_file_name', editType: 'text', editConfig: { maxLength: 80 }, render: (r) => r.EF_file_name ?? '—' },
      { key: 'billed', label: '% Facturado', field: 'billed_percentage', editType: 'text', editConfig: { maxLength: 30 }, render: (r) => r.billed_percentage ?? '—' },
      /* Facturado (boolean) — Gerente/Líder only: hidden entirely from Desarrolladores
         (write also blocked server-side by enforce_developer_requirement_update_columns). */
      ...(isGerenteOrLider ? [{
        key: 'fully_billed',
        label: 'Facturado',
        field: 'fully_billed',
        editType: 'toggle',
        render: (r) => (
          <span
            className={`req-billed-check${r.fully_billed ? ' req-billed-check--on' : ''}`}
            aria-label={r.fully_billed ? 'Facturado en su totalidad' : 'No facturado'}
            title={r.fully_billed ? 'Facturado en su totalidad' : 'No facturado'}
          >
            {r.fully_billed && <IconCheck />}
          </span>
        ),
      }] : []),
      { key: 'comments', label: 'Comentarios', field: 'comment', editType: 'comment', editConfig: { maxLength: COMMENT_MAXLEN }, render: (r) => r.comment ? <div className="req-cell-clamp">{r.comment}</div> : '—', wide: true },
      {
        key: 'docs',
        label: 'Documentos',
        render: (r) => {
          const showButton = editMode && isGerenteOrLider;
          const isFetching = fetchingUrls.has(r.id);
          if (showButton) {
            return (
              <div className="req-docs-edit-cell">
                {r.docs_hyperlink && (
                  <a
                    href={r.docs_hyperlink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="req-doc-link"
                    title="Abrir documentos"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconExtLink /> Ver
                  </a>
                )}
                <button
                  className="req-fetch-url-btn"
                  disabled={isFetching}
                  onClick={(e) => { e.stopPropagation(); handleFetchUrl(r); }}
                >
                  {isFetching ? <span className="req-fetch-url-spinner" /> : 'Obtener URL'}
                </button>
              </div>
            );
          }
          if (!r.docs_hyperlink) return '—';
          return (
            <a
              href={r.docs_hyperlink}
              target="_blank"
              rel="noopener noreferrer"
              className="req-doc-link"
              title="Abrir documentos"
            >
              <IconExtLink /> Ver
            </a>
          );
        },
      },
    );

    if (isDesarrollador) {
      return cols.map((col) => {
        if (!col.editType) return col;
        if (DEV_ALLOWED_FIELDS.has(col.field)) return col;
        const { field, editType, editConfig, ...rest } = col;
        return rest;
      });
    }

    return cols;
  }, [isGerenteOrLider, isDesarrollador, DEV_ALLOWED_FIELDS, rootRefMap, editMode, deleteMode, openDeleteModal, fetchingUrls, handleFetchUrl, cancelEditing]);

  /* ── Editable columns indexed by DB field ──
     Drives the detail-view in-place editing. Built from `columns`, so it
     inherits the exact per-role permission gating already encoded there
     (Desarrollador → only progress/status/comment; client-only fields for
     Gerente/Líder; etc.). */
  const editableColsByField = useMemo(() => {
    const map = {};
    for (const col of columns) {
      if (col.field && col.editType) map[col.field] = col;
    }
    return map;
  }, [columns]);

  /* ── Excel export ─────────────────────────────────
   * Exports exactly the columns/rows currently shown in the table (role-aware,
   * filtered — not just the current page). exceljs is imported lazily so it
   * stays out of the main bundle. Reuses the same professional format as the
   * Reporte de Actividades export (ROYAL header, zebra rows, thin borders,
   * frozen title block, autofilter, Caribbean totals row). */
  const cellValueForExport = useCallback((col, row) => {
    switch (col.key) {
      case 'reference_id': return row.reference_id || '—';
      case 'client': return row.project?.client?.name ?? '—';
      case 'project': return row.project?.name ?? '—';
      case 'root': return row.root_requirement_id ? (rootRefMap[row.root_requirement_id] ?? '—') : '—';
      case 'description': return row.description || '—';
      case 'hours_dev': return row.estimated_completion_hours_dev != null ? Number(row.estimated_completion_hours_dev) : '—';
      case 'allow_extended_hours': return row.allow_extended_hours ? 'Sí' : 'No';
      case 'hours_client': return row.estimated_completion_hours_client != null ? Number(row.estimated_completion_hours_client) : '—';
      case 'specialization': return row.specialization?.name ?? '—';
      case 'requirement_type': return row.requirement_type ?? '—';
      case 'billing_type': return row.billing_type?.name ?? '—';
      case 'status': return row.status_rel?.name ?? '—';
      case 'status_client': return row.status_client_rel?.name ?? '—';
      case 'created_at': return formatDate(row.created_at);
      case 'VoBo_estimated_date': return formatDate(row.VoBo_estimated_date);
      case 'construction_start_date': return formatDate(row.construction_start_date);
      case 'dev_delivery_date': return formatDate(row.dev_delivery_date);
      case 'client_delivery_date': return formatDate(row.client_delivery_date);
      case 'progress': return row.progress_percentage != null ? `${row.progress_percentage}%` : '—';
      case 'estimator': return row.estimator?.full_name ?? '—';
      case 'leader': return row.leader?.full_name ?? '—';
      case 'devs': {
        const names = (row.requirement_devs || []).filter((d) => d.user?.full_name).map((d) => d.user.full_name);
        return names.length ? names.join(', ') : '—';
      }
      case 'functional': return row.functional_user_name ?? '—';
      case 'module': return row.module ?? '—';
      case 'ef_version': return row.EF_version ?? '—';
      case 'ef_file': return row.EF_file_name ?? '—';
      case 'billed': return row.billed_percentage ?? '—';
      case 'fully_billed': return row.fully_billed ? 'Sí' : 'No';
      case 'comments': return row.comment || '—';
      case 'docs': return row.docs_hyperlink || '—';
      default: return '—';
    }
  }, [rootRefMap]);

  async function exportToExcel() {
    if (!filteredRows.length || exporting) return;
    setExporting(true);
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;

      const ROYAL = 'FF00115D';
      const CARIBBEAN = 'FF00CFC1';
      const ZEBRA = 'FFF8F9FA';
      const thinBorder = {
        top: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        left: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        bottom: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        right: { style: 'thin', color: { argb: 'FFD9DCE5' } },
      };

      /* Per-column widths & alignment, keyed on the same column definitions
         that build the on-screen table (so a Desarrollador export omits the
         same columns their table hides). */
      const WIDE_KEYS = new Set(['description', 'comments', 'devs', 'docs']);
      const NUM_KEYS = new Set(['hours_dev', 'hours_client']);
      const CENTER_KEYS = new Set(['root', 'allow_extended_hours', 'created_at', 'VoBo_estimated_date', 'construction_start_date', 'dev_delivery_date', 'client_delivery_date', 'progress', 'fully_billed']);

      const wb = new ExcelJS.Workbook();
      wb.creator = 'T.R.A.C.K.';
      wb.created = new Date();

      const ws = wb.addWorksheet('Bitácora de Requerimientos', {
        views: [{ state: 'frozen', ySplit: 4 }],
      });

      const colCount = columns.length;
      columns.forEach((c, i) => {
        ws.getColumn(i + 1).width = WIDE_KEYS.has(c.key) ? 42 : NUM_KEYS.has(c.key) ? 12 : 18;
      });

      /* Title block */
      ws.mergeCells(1, 1, 1, colCount);
      const titleCell = ws.getCell(1, 1);
      titleCell.value = 'T.R.A.C.K. — Bitácora de Requerimientos';
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(1).height = 30;

      ws.mergeCells(2, 1, 2, colCount);
      const countCell = ws.getCell(2, 1);
      countCell.value = `${filteredRows.length} requerimiento${filteredRows.length !== 1 ? 's' : ''}`;
      countCell.font = { bold: true, size: 11, color: { argb: ROYAL } };
      countCell.alignment = { horizontal: 'center' };

      /* Applied-filters subtitle — mirrors the active filter bar */
      const statusNames = filterStatus
        .map((id) => statusOptions.find((s) => String(s.id) === String(id))?.name)
        .filter(Boolean);
      const devNames = filterDevs
        .map((id) => devPickerOptions.find((u) => String(u.id) === String(id))?.full_name)
        .filter(Boolean);
      const appliedFilters = [
        search.trim() && `Folio: ${search.trim()}`,
        statusNames.length && `Estatus: ${statusNames.join(', ')}`,
        filterClient && `Cliente: ${clientOptions.find((c) => String(c.id) === String(filterClient))?.name || ''}`,
        filterLeader && `Líder: ${leaderOptions.find((l) => String(l.id) === String(filterLeader))?.full_name || ''}`,
        filterProject && `Proyecto: ${projectOptions.find((p) => String(p.id) === String(filterProject))?.name || ''}`,
        filterEstimator && `Estimador: ${estimatorOptions.find((u) => String(u.id) === String(filterEstimator))?.full_name || ''}`,
        devNames.length && `Desarrolladores: ${devNames.join(', ')}`,
        billedFilterActive && `Facturación: ${filterBilled === 'true' ? 'Facturados' : 'No facturados'}`,
        billingTypeFilterActive && `Tipo de Facturación: ${selectedBillingTypeLabel}`,
      ].filter(Boolean);
      ws.mergeCells(3, 1, 3, colCount);
      const filtersCell = ws.getCell(3, 1);
      filtersCell.value = `${appliedFilters.length ? `Filtros — ${appliedFilters.join('  |  ')}` : 'Sin filtros aplicados'}   ·   Generado el ${new Date().toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}`;
      filtersCell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
      filtersCell.alignment = { horizontal: 'center' };

      /* Header row */
      const headerRow = ws.getRow(4);
      columns.forEach((c, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = c.label;
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = thinBorder;
      });
      headerRow.height = 22;

      /* Data rows — same order as the on-screen table */
      filteredRows.forEach((row, idx) => {
        const r = ws.getRow(5 + idx);
        columns.forEach((col, i) => {
          const cell = r.getCell(i + 1);
          cell.value = cellValueForExport(col, row);
          cell.font = { size: 10 };
          cell.border = thinBorder;
          cell.alignment = { vertical: 'top', wrapText: WIDE_KEYS.has(col.key) };
          if (idx % 2 === 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
          }
          if (NUM_KEYS.has(col.key)) {
            if (typeof cell.value === 'number') cell.numFmt = '0.00';
            cell.alignment = { ...cell.alignment, horizontal: 'right' };
          } else if (CENTER_KEYS.has(col.key)) {
            cell.alignment = { ...cell.alignment, horizontal: 'center' };
          }
        });
      });

      /* Totals row — sum of the estimated-hours columns present for this role */
      const hoursDevIdx = columns.findIndex((c) => c.key === 'hours_dev');
      const hoursClientIdx = columns.findIndex((c) => c.key === 'hours_client');
      const totalsRow = ws.getRow(5 + filteredRows.length);
      totalsRow.getCell(1).value = 'TOTALES';
      if (hoursDevIdx >= 0) {
        totalsRow.getCell(hoursDevIdx + 1).value = round2(
          filteredRows.reduce((acc, r) => acc + (Number(r.estimated_completion_hours_dev) || 0), 0)
        );
      }
      if (hoursClientIdx >= 0) {
        totalsRow.getCell(hoursClientIdx + 1).value = round2(
          filteredRows.reduce((acc, r) => acc + (Number(r.estimated_completion_hours_client) || 0), 0)
        );
      }
      for (let i = 1; i <= colCount; i++) {
        const cell = totalsRow.getCell(i);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CARIBBEAN } };
        cell.font = { bold: true, size: 10, color: { argb: ROYAL } };
        cell.border = thinBorder;
        if (i === hoursDevIdx + 1 || i === hoursClientIdx + 1) {
          cell.numFmt = '0.00';
          cell.alignment = { horizontal: 'right' };
        }
      }

      ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + filteredRows.length, column: colCount } };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Bitacora_de_Requerimientos_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast('error', 'No se pudo generar el archivo de Excel. Intenta de nuevo.');
    } finally {
      setExporting(false);
    }
  }

  /* ── Count label ── */
  function countLabel() {
    if ((search.trim() || hasActiveFilters) && filteredRows.length !== rows.length) {
      return `${filteredRows.length} de ${rows.length} requerimientos`;
    }
    return `${rows.length} requerimiento${rows.length !== 1 ? 's' : ''}`;
  }

  /* ── Skeleton rows ── */
  function renderSkeletonRows() {
    return Array.from({ length: 10 }).map((_, i) => (
      <tr key={i} className="req-skeleton-row">
        {columns.map((col) => (
          <td key={col.key} className={col.key === 'reference_id' ? 'req-td--sticky' : ''}>
            <div className="req-skeleton-cell" />
          </td>
        ))}
      </tr>
    ));
  }

  /* ── Edit-mode modals + toast ──
     Shared by the table view and the detail view so both drive the same edit
     handlers, options and PA notifications. Portaled to document.body in each
     branch (filter modals stay table-only). */
  const commentEditOpen = editingCell?.editType === 'comment';
  const commentEditRow = commentEditOpen ? rows.find((r) => r.id === editingCell.rowId) : null;
  const commentViewRow = commentViewId != null ? rows.find((r) => r.id === commentViewId) : null;

  const editModals = (
    <>
      <CommentEditModal
        open={commentEditOpen}
        folio={commentEditRow?.reference_id ?? ''}
        value={editValue}
        original={commentEditRow?.comment ?? ''}
        onChange={setEditValue}
        onSave={() => {
          if (editingCell) saveCell(editingCell.rowId, editingCell.field, editValue.trim() || null, 'comment');
        }}
        onClose={cancelEditing}
      />
      <CommentViewModal row={commentViewRow} onClose={() => setCommentViewId(null)} />
      <StatusPickerModal
        open={editingCell?.editType === 'status'}
        options={statusOptions}
        value={editingCell?.editType === 'status' ? editValue : ''}
        onSelect={(statusId) => {
          if (editingCell) saveCell(editingCell.rowId, editingCell.field, statusId, 'status');
        }}
        onClose={cancelEditing}
      />
      <SpecializationPickerModal
        open={editingCell?.editType === 'specialization'}
        options={specializationOptions}
        value={editingCell?.editType === 'specialization' ? editValue : ''}
        onSelect={(specId) => {
          if (editingCell) saveCell(editingCell.rowId, editingCell.field, specId, 'specialization');
        }}
        onClose={cancelEditing}
      />
      <SpecializationPickerModal
        open={editingCell?.editType === 'billing_type'}
        title="Seleccionar Tipo de Facturación"
        emptyLabel="Sin tipo de facturación"
        options={activeBillingTypeOptions}
        value={editingCell?.editType === 'billing_type' ? editValue : ''}
        onSelect={(billingTypeId) => {
          if (editingCell) saveCell(editingCell.rowId, editingCell.field, billingTypeId, 'billing_type');
        }}
        onClose={cancelEditing}
      />
      <EstimatorPickerModal
        open={editingCell?.editType === 'estimator'}
        options={estimatorOptions}
        value={editingCell?.editType === 'estimator' ? editValue : ''}
        onSelect={(uid) => {
          if (editingCell) saveEstimator(editingCell.rowId, uid);
        }}
        onClose={cancelEditing}
      />
      <EstimatorPickerModal
        title="Seleccionar Líder"
        hideEmptyOption
        open={editingCell?.editType === 'lider'}
        options={leaderOptions}
        value={editingCell?.editType === 'lider' ? editValue : ''}
        onSelect={(uid) => {
          if (editingCell) saveLider(editingCell.rowId, uid);
        }}
        onClose={cancelEditing}
      />
      <DatePickerModal
        open={editingCell?.editType === 'date'}
        value={editingCell?.editType === 'date' ? editValue : ''}
        onSelect={(dateStr) => {
          if (editingCell) saveCell(editingCell.rowId, editingCell.field, dateStr, 'date');
        }}
        onClose={cancelEditing}
      />
      <DevPickerModal
        open={editingCell?.editType === 'devs'}
        options={devPickerOptions}
        selectedIds={devSelection}
        onConfirm={(ids) => { if (editingCell) saveDevs(editingCell.rowId, ids); }}
        onClose={cancelEditing}
      />
      <RootPickerModal
        open={editingCell?.editType === 'root'}
        options={editingCell?.editType === 'root'
          ? rootOptions.filter((r) => r.id !== editingCell.rowId)
          : []}
        currentId={editingCell?.editType === 'root'
          ? (rows.find((r) => r.id === editingCell.rowId)?.root_requirement_id ?? null)
          : null}
        onConfirm={(id) => { if (editingCell) saveRoot(editingCell.rowId, id); }}
        onClose={cancelEditing}
      />
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </>
  );

  if (view === 'detail' && selectedRow) {
    /* Find the live version of the row (realtime may have updated it) */
    const liveRow = rows.find((r) => r.id === selectedRow.id) || selectedRow;
    return (
      <div className="req-page">
        <Navbar />
        <main className="req-main">
          <RequirementDetail
            row={liveRow}
            rootRefMap={rootRefMap}
            isGerenteOrLider={isGerenteOrLider}
            onBack={() => { setView('table'); setSelectedRow(null); setDetailEditMode(false); cancelEditing(); }}
            editControls={canEditFields ? {
              editMode: detailEditMode,
              setEditMode: setDetailEditMode,
              canEditFields,
              /* Desarrollador: estimator-only rows (not assigned as dev) are read-only */
              canRowEdit: canDevEditRow(liveRow),
              isGerenteOrLider,
              editableColsByField,
              editingCell,
              editValue,
              setEditValue,
              startEditing,
              commitEdit,
              cancelEditing,
              saveCell,
              fetchingUrls,
              handleFetchUrl,
              canDuplicate: isGerenteOrLider,
              onDuplicate: (srcRow) => {
                cancelEditing();
                setDetailEditMode(false);
                setDuplicateSource(srcRow);
                setView('duplicate');
              },
            } : undefined}
          />
          {createPortal(editModals, document.body)}
        </main>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="req-page">
        <Navbar />
        <main className="req-main">
          <RequirementForm
            onBack={() => setView('table')}
            onCreated={() => fetchData()}
          />
        </main>
      </div>
    );
  }

  if (view === 'duplicate' && duplicateSource) {
    return (
      <div className="req-page">
        <Navbar />
        <main className="req-main">
          <RequirementDuplicateForm
            source={duplicateSource}
            onBack={() => { setView('table'); setDuplicateSource(null); }}
            onCreated={() => fetchData()}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="req-page req-page--list">
      <Navbar />

      <main className="req-main">
        {/* ── Top bar: search + action buttons ── */}
        <div className="req-topbar">
          <div className="req-search-wrap">
            <IconSearch />
            <input
              type="text"
              className="req-search"
              placeholder="Buscar por folio…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {!loading && (
            <span className="req-count">{countLabel()}</span>
          )}

          <div className="req-topbar-actions">
            {isGerenteOrLider && (
              <button
                className="req-btn req-btn--excel"
                onClick={exportToExcel}
                disabled={exporting || loading || filteredRows.length === 0}
                title={filteredRows.length === 0 ? 'No hay requerimientos para exportar' : 'Exportar a Excel'}
              >
                {exporting ? <span className="req-fetch-url-spinner" /> : <IconDownload />}
                <span>{exporting ? 'Exportando…' : 'Exportar a Excel'}</span>
              </button>
            )}
            {/* Editar Campos / Borrar Requerimiento / Nuevo Requerimiento are
                mutually exclusive: while one mode is active the other two
                buttons are disabled, so the modes can never overlap. */}
            {isGerente && (
              <button
                className={`req-btn ${deleteMode ? 'req-btn--delete-active' : 'req-btn--delete'}`}
                onClick={() => setDeleteMode((m) => !m)}
                disabled={editMode}
                title={editMode ? 'Sal del modo de edición para borrar requerimientos' : undefined}
              >
                <IconTrash />
                <span>{deleteMode ? 'Cancelar Borrado' : 'Borrar Requerimiento'}</span>
              </button>
            )}
            {canEditFields && (
              <button
                className={`req-btn ${editMode ? 'req-btn--edit-active' : 'req-btn--secondary'}`}
                onClick={() => { setEditMode((m) => !m); cancelEditing(); }}
                disabled={deleteMode}
                title={deleteMode ? 'Sal del modo de borrado para editar campos' : undefined}
              >
                <IconEdit />
                <span>{editMode ? 'Dejar de Editar' : 'Editar Campos'}</span>
              </button>
            )}
            {isGerenteOrLider && (
              <button
                className="req-btn req-btn--primary"
                onClick={() => setView('create')}
                disabled={editMode || deleteMode}
                title={editMode ? 'Sal del modo de edición para crear un requerimiento' : deleteMode ? 'Sal del modo de borrado para crear un requerimiento' : undefined}
              >
                <IconPlus />
                <span>Nuevo Requerimiento</span>
              </button>
            )}
          </div>
        </div>

        {/* ── Delete-mode banner ── */}
        {deleteMode && (
          <div className="req-delete-banner" role="status">
            <IconTrash />
            <span>Modo de borrado activo — haz clic en el requerimiento que deseas eliminar.</span>
          </div>
        )}

        {/* ── Filter bar ── */}
        <div className="req-filters">
          <div className="req-filter-group">
            <IconFilter />

            <button
              className={`req-picker-trigger${filterStatus.length > 0 ? ' req-picker-trigger--active' : ''}`}
              onClick={() => setStatusModalOpen(true)}
            >
              <span className="req-picker-trigger-label">{selectedStatusLabel}</span>
              <IconChevronDown />
            </button>

            <button
              className={`req-picker-trigger${filterClient ? ' req-picker-trigger--active' : ''}`}
              onClick={() => setClientModalOpen(true)}
            >
              <span className="req-picker-trigger-label">{selectedClientLabel}</span>
              <IconChevronDown />
            </button>

            <div className="req-select-wrap">
              <select
                className="req-select"
                value={filterLeader}
                onChange={(e) => setFilterLeader(e.target.value)}
              >
                <option value="">Líder</option>
                {leaderOptions.map((l) => (
                  <option key={l.id} value={l.id}>{l.full_name}</option>
                ))}
              </select>
              <IconChevronDown />
            </div>

            <button
              className={`req-picker-trigger${filterProject ? ' req-picker-trigger--active' : ''}`}
              onClick={() => setProjectModalOpen(true)}
            >
              <span className="req-picker-trigger-label">{selectedProjectLabel}</span>
              <IconChevronDown />
            </button>

            <button
              className={`req-picker-trigger${filterEstimator ? ' req-picker-trigger--active' : ''}`}
              onClick={() => setEstimatorModalOpen(true)}
            >
              <span className="req-picker-trigger-label">{selectedEstimatorLabel}</span>
              <IconChevronDown />
            </button>

            <button
              className={`req-picker-trigger${filterDevs.length > 0 ? ' req-picker-trigger--active' : ''}`}
              onClick={() => setDevFilterModalOpen(true)}
            >
              <span className="req-picker-trigger-label">{selectedDevsLabel}</span>
              <IconChevronDown />
            </button>

            {isGerenteOrLider && (
              <button
                className={`req-picker-trigger${filterBilled ? ' req-picker-trigger--active' : ''}`}
                onClick={() => setBilledModalOpen(true)}
              >
                <span className="req-picker-trigger-label">{selectedBilledLabel}</span>
                <IconChevronDown />
              </button>
            )}

            {isGerenteOrLider && (
              <button
                className={`req-picker-trigger${filterBillingType ? ' req-picker-trigger--active' : ''}`}
                onClick={() => setBillingTypeModalOpen(true)}
              >
                <span className="req-picker-trigger-label">{selectedBillingTypeLabel}</span>
                <IconChevronDown />
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <button className="req-clear-filters" onClick={clearAllFilters}>
              <IconClearFilter />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className="req-table-wrap">
            <table className="req-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className={col.key === 'reference_id' ? 'req-th--sticky' : ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderSkeletonRows()}</tbody>
            </table>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="req-empty">
            {search || hasActiveFilters
              ? 'No se encontraron requerimientos con los filtros aplicados.'
              : 'No hay requerimientos registrados.'}
          </div>
        ) : (
          <>
            <div className="req-table-wrap">
              <table
                ref={tableRef}
                className={`req-table${editMode ? ' req-table--editing' : ''}`}
                onMouseOver={editMode ? handleTableHover : undefined}
                onMouseLeave={editMode ? clearHoverHl : undefined}
              >
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} className={[col.wide ? 'req-th--wide' : '', col.key === 'reference_id' ? 'req-th--sticky' : ''].filter(Boolean).join(' ')}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row) => (
                    <tr
                      key={row.id}
                      className={deleteMode ? 'req-tr--deletable' : undefined}
                      onClick={deleteMode ? () => openDeleteModal(row) : undefined}
                    >
                      {columns.map((col) => {
                        const canEdit = editMode && col.editType && canDevEditRow(row);
                        const isEditing = editingCell?.rowId === row.id && editingCell?.field === col.field;
                        const isInline = isEditing && (col.editType === 'text' || col.editType === 'number' || col.editType === 'progress');
                        const isToggle = col.editType === 'toggle';
                        /* Clamped Comentarios cell → read-only view modal, but
                           only when the cell click isn't already claimed by
                           edit mode (startEditing) or delete mode (row click). */
                        const isCommentView = col.key === 'comments' && !canEdit && !deleteMode && !!row.comment;

                        return (
                          <td
                            key={col.key}
                            className={`${col.key === 'reference_id' ? 'req-td--sticky' : ''}${col.wide ? ' req-td--wide' : ''}${canEdit ? ' req-td--editable' : ''}${isEditing ? ' req-td--editing' : ''}${isCommentView ? ' req-td--comment-view' : ''}`}
                            onClick={
                              canEdit && !isEditing
                                ? isToggle
                                  ? () => saveCell(row.id, col.field, !row[col.field], 'toggle')
                                  : () => startEditing(row, col)
                                : isCommentView
                                  ? () => setCommentViewId(row.id)
                                  : undefined
                            }
                          >
                            {isInline ? (
                              <input
                                autoFocus
                                className="req-inline-input"
                                type={col.editType === 'text' ? 'text' : 'number'}
                                value={editValue}
                                maxLength={col.editConfig?.maxLength}
                                min={col.editConfig?.min}
                                max={col.editConfig?.max}
                                step={col.editConfig?.step || (col.editType === 'number' ? '1' : undefined)}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => commitEdit(row, col)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') cancelEditing();
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              col.render(row)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="req-pagination-wrap">
                <span className="req-page-info">
                  Página {safePage} de {totalPages}
                </span>
                <div className="req-pagination">
                  <button
                    className="req-page-nav"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    aria-label="Página anterior"
                  >
                    <IconChevronLeft />
                  </button>

                  {pageList.map((item, i) =>
                    item === '...' ? (
                      <span key={`ellipsis-${i}`} className="req-page-ellipsis">…</span>
                    ) : (
                      <button
                        key={item}
                        className={`req-page-btn${safePage === item ? ' req-page-btn--active' : ''}`}
                        onClick={() => setPage(item)}
                        aria-current={safePage === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    className="req-page-nav"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    aria-label="Página siguiente"
                  >
                    <IconChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {/* ── Modals & toast are rendered through a portal to document.body so
             their position:fixed resolves to the viewport — not to .req-main,
             whose entrance animation leaves a permanent transform that would
             otherwise act as the containing block and mis-position them. ── */}
        {createPortal(
          <>
        {/* ── Filter picker modals ── */}
        <StatusFilterModal
          open={statusModalOpen}
          options={statusOptions}
          selectedIds={filterStatus}
          onConfirm={(ids) => setFilterStatus(ids.map(String))}
          onClose={() => setStatusModalOpen(false)}
        />
        <FilterPickerModal
          open={clientModalOpen}
          title="Seleccionar Cliente"
          options={clientOptions}
          value={filterClient}
          onSelect={(id) => setFilterClient(id ? String(id) : '')}
          onClose={() => setClientModalOpen(false)}
          labelKey="name"
        />
        <FilterPickerModal
          open={projectModalOpen}
          title="Seleccionar Proyecto"
          options={filteredProjectOptions}
          value={filterProject}
          onSelect={handleProjectSelect}
          onClose={() => setProjectModalOpen(false)}
          labelKey="name"
        />
        <EstimatorPickerModal
          open={estimatorModalOpen}
          title="Filtrar por Estimador"
          emptyLabel="Todos los estimadores"
          options={estimatorOptions}
          value={filterEstimator}
          onSelect={(uid) => setFilterEstimator(uid ? String(uid) : '')}
          onClose={() => setEstimatorModalOpen(false)}
        />
        <DevPickerModal
          open={devFilterModalOpen}
          title="Filtrar por Desarrollador"
          confirmLabel="Aplicar"
          emptyCountLabel="Todos los desarrolladores"
          options={devPickerOptions}
          selectedIds={filterDevs}
          onConfirm={(ids) => setFilterDevs(ids.map(String))}
          onClose={() => setDevFilterModalOpen(false)}
        />
        <BilledFilterModal
          open={billedModalOpen}
          value={filterBilled}
          onSelect={(v) => setFilterBilled(v)}
          onClose={() => setBilledModalOpen(false)}
        />
        <FilterPickerModal
          open={billingTypeModalOpen}
          title="Filtrar por Tipo de Facturación"
          options={billingTypeFilterOptions}
          value={filterBillingType}
          onSelect={(id) => setFilterBillingType(id ? String(id) : '')}
          onClose={() => setBillingTypeModalOpen(false)}
          labelKey="name"
        />

        {/* ── Delete confirmation modal (table view only) ── */}
        {deleteTarget && (
          <DeleteRequirementModal
            row={deleteTarget}
            blockers={deleteBlockers}
            deleting={deleting}
            onClose={closeDeleteModal}
            onConfirm={confirmDelete}
          />
        )}

        {/* ── Edit mode modals + toast (shared with detail view) ── */}
        {editModals}
          </>,
          document.body
        )}
      </main>
    </div>
  );
}

/* ── Contrast helper for status badges ── */
function getContrastColor(hex) {
  if (!hex) return '#222222';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  /* relative luminance */
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#222222' : '#FFFFFF';
}

export default Requirements;
